import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Info } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { LEGAL_PAGES, LEGAL_NAV } from "@/data/legal";

const LegalPage = () => {
  const { slug } = useParams();
  const doc = LEGAL_PAGES[slug];

  if (!doc) {
    return (
      <div className="py-32 text-center" data-testid="legal-not-found">
        <h1 className="font-serif text-3xl text-teal">Page not found</h1>
        <Link to="/" className="mt-4 inline-block text-sm text-teal underline underline-offset-4">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`legal-${slug}`}>
      <Seo title={doc.title} noindex />
      <section className="bg-sand-light/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
            <ArrowLeft className="w-4 h-4" /> Plan First NSW
          </Link>
          <FadeIn>
            <Eyebrow>Legal & Policies</Eyebrow>
            <h1 className="mt-3 font-serif text-3xl sm:text-4xl text-teal tracking-tight">{doc.title}</h1>
            <p className="mt-4 text-charcoal/75 leading-relaxed max-w-2xl">{doc.intro}</p>
            <p
              className="mt-6 inline-flex items-start gap-2.5 bg-sand-light px-4 py-3 text-xs text-charcoal/70 leading-relaxed"
              data-testid="legal-draft-banner"
            >
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-teal" />
              Draft prepared for Australian legal review · Last updated October 2026. Nothing on this
              page limits your rights under the Australian Consumer Law.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-10">
            {doc.sections.map((s, i) => (
              <FadeIn key={s.h} delay={i * 0.03}>
                <div>
                  <h2 className="font-serif text-xl text-teal border-b border-cloud pb-2">
                    {String(i + 1).padStart(2, "0")} · {s.h}
                  </h2>
                  <div className="mt-4 space-y-3">
                    {s.body.map((p, j) => (
                      <p key={j} className="text-sm text-charcoal/80 leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn className="mt-14">
            <div className="bg-sand-light/50 border border-cloud p-6">
              <Eyebrow>Related policies</Eyebrow>
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                {LEGAL_NAV.filter(([s]) => s !== slug).map(([s, label]) => (
                  <Link
                    key={s}
                    to={`/legal/${s}`}
                    className="text-xs font-mono uppercase tracking-[0.12em] text-teal underline underline-offset-4 hover:text-teal-dark"
                  >
                    {label}
                  </Link>
                ))}
              </div>
              <p className="mt-5 text-xs text-charcoal/60">
                Questions about any of these documents? Email{" "}
                <a href="mailto:hello@planfirstnsw.com.au" className="text-teal underline underline-offset-4">
                  hello@planfirstnsw.com.au
                </a>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
